import { langFallback$FetchWithCache } from '../lang/utils/langfetch'
import { logger } from './logger'

export interface FacetRemovalRule {
  facet: string
  redirect: string | null
  category_all: boolean
  category_glamping: boolean
  category_campsites: boolean
  category_ids: number[]
}

export async function getFacetRules(
  facet: string,
  langCode: string,
): Promise<FacetRemovalRule[]> {
  try {
    const response = await langFallback$FetchWithCache<{
      results: FacetRemovalRule[]
    }>(langCode, `/internal/facets/removal-rules/?facet=${facet}`)
    return response.results
  } catch (error) {
    logger('facets.rules.error', error)
    return []
  }
}

/**
 * Check if facets would be removed for a specific target category/scenario
 * This follows the same logic used in SearchBreadcrumbs.vue
 *
 * @param rules - Array of facet removal rules
 * @param categoryIds - Currently selected category IDs
 * @param targetScenario - What scenario to check: 'all-campsites', 'glamping', or specific category IDs
 * @returns true if facets would be removed (causing redirect), false otherwise
 */
export function shouldRemoveFacets(
  rules: FacetRemovalRule[],
  categoryIds: string[],
  targetScenario: 'all-campsites' | 'glamping' | string[] = 'all-campsites',
): boolean {
  if (!rules || rules.length === 0) {
    return false
  }

  return rules.some((rule) => {
    // Facet removed for all categories
    if (rule.category_all) {
      return true
    }

    // Check scenario-specific rules
    if (targetScenario === 'all-campsites') {
      // Facet removed for "campsites" (no specific category)
      return rule.category_campsites
    } else if (targetScenario === 'glamping') {
      // Facet removed for glamping
      return rule.category_glamping
    } else if (Array.isArray(targetScenario)) {
      // Check if any of the target category IDs would have the facet removed
      return targetScenario.some((id) => rule.category_ids.includes(Number(id)))
    }

    return false
  })
}
